import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ListFilter, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/atoms/Tooltip";
import { FilterBarBoundary, useFilterHighlight } from "@/components/molecules/FilterBar";

interface Props {
  /** Column label, repeated as the panel's heading so the panel stands alone. */
  label: string;
  /** Hover hint on the trigger — what this filter matches on. */
  tooltip?: string;
  /** Whether the column currently holds a filter value. Drives the accent tint. */
  active: boolean;
  /** Clears just this column's filter. */
  onClear: () => void;
  /** Panel width in px. Widen for filters with two fields side by side. */
  width?: number;
  /** The filter control(s) — ordinary `SearchInput`/`MenuSelect`/`Input` etc. */
  children: ReactNode;
}

/**
 * Filter control that lives in a table's column header, beside the label.
 *
 * The trigger is an icon rather than the control itself so a header stays the
 * height of its text and the label keeps the full column width; the control
 * opens in a panel anchored under it. An applied filter tints the icon, which
 * is the only cue that a column is narrowing the list once the panel is shut.
 *
 * The panel is portaled to the body: a table header is a scroll container with
 * `overflow` and stacking contexts of its own, and a panel positioned inside it
 * would be clipped by the first column that scrolls out of view.
 */
export function ColumnFilter({ label, tooltip, active, onClear, width = 240, children }: Props) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<{ top: number; left: number } | null>(null);
  // The panel is anchored on this wrapper rather than on the button itself:
  // `Tooltip` clones its child and takes the child's `ref` for its own
  // positioning, so a ref on the button would never reach us.
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Registers with the surrounding FilterScope so the column counts towards the
  // "N filters" badge and Clear Filters. Done here, on a control that is always
  // mounted, rather than on the fields inside the panel — those unmount when the
  // panel closes, which would drop the filter from the tally while it still
  // applies. `FilterBarBoundary` below stops them registering a second time.
  useFilterHighlight(active);

  const updatePlacement = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    // Right-aligned to the trigger, then pulled back inside the viewport for
    // the last column in a wide table.
    const left = Math.max(pad, Math.min(rect.right - width, window.innerWidth - width - pad));
    setPlacement({ top: rect.bottom + 6, left });
  }, [width]);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    updatePlacement();
    window.addEventListener("resize", updatePlacement);
    // Capture phase: the table body, the page and the window each scroll the
    // trigger, and only a capturing listener sees all three.
    window.addEventListener("scroll", updatePlacement, true);
    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement, true);
    };
  }, [open, updatePlacement]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      // A MenuSelect or date picker inside the panel portals its dropdown to
      // the body, so a click on an option lands outside both refs — closing
      // here would unmount the dropdown before its click fired and swallow the
      // selection. Every such surface is marked, and marks keep the panel open.
      if ((target as HTMLElement).closest?.("[data-column-filter-panel],[data-floating-surface]")) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const trigger = (
    <button
      type="button"
      aria-label={active ? `${label} filter applied` : `Filter by ${label}`}
      aria-expanded={open}
      onClick={(e) => {
        // Header cells make the label a sort button; don't sort on the way past.
        e.stopPropagation();
        setOpen((was) => !was);
      }}
      className={cn(
        "relative inline-flex items-center justify-center h-6 w-6 shrink-0 rounded-md border transition-colors",
        active
          ? "border-bridge-accent/60 bg-bridge-accent/10 text-bridge-accent"
          : "border-transparent text-bridge-muted hover:border-bridge-border hover:bg-bridge-surface/80 hover:text-bridge-fg",
        open && !active && "border-bridge-border bg-bridge-surface text-bridge-fg",
      )}
    >
      <ListFilter size={13} strokeWidth={2.5} />
      {active && (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-bridge-accent ring-2 ring-bridge-bg"
        />
      )}
    </button>
  );

  return (
    <>
      <span ref={anchorRef} className="inline-flex">
        {tooltip ? (
          <Tooltip side="top" hoverOnly content={tooltip}>
            {trigger}
          </Tooltip>
        ) : (
          trigger
        )}
      </span>

      {open &&
        placement &&
        createPortal(
          <FilterBarBoundary>
            <div
              ref={panelRef}
              data-column-filter-panel
              className="fixed z-[200] rounded-xl border border-bridge-border/70 bg-white p-3 shadow-lg shadow-slate-200/80"
              style={{ top: placement.top, left: placement.left, width }}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-bridge-muted">
                  {label}
                </span>
                {active && (
                  <button
                    type="button"
                    onClick={onClear}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-bridge-accent hover:underline"
                  >
                    <X size={11} />
                    Clear
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2 normal-case tracking-normal">{children}</div>
            </div>
          </FilterBarBoundary>,
          document.body,
        )}
    </>
  );
}

/** Labelled row inside a {@link ColumnFilter} panel, for panels holding two fields. */
export function ColumnFilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-bridge-subtle">
        {label}
      </span>
      {children}
    </label>
  );
}
